import { create } from 'zustand'

export interface ErpTab {
  key: string // 路由 path
  title: string
  path: string
}

export const DASHBOARD_PATH = '/dashboard'

/** 不生成页签的路径（如根路径重定向、登录页） */
export const EXCLUDED_TAB_PATHS = ['/', '/login']

/** 已失效的路由 path 前缀黑名单：持久化恢复时剔除这些页签（含其 /new、/edit/ 等子路径） */
export const INVALID_TAB_PATHS = ['/production_management/production-orders']

/** 判断持久化的 tab.key 是否命中失效路由黑名单 */
function isInvalidTabPath(key: string): boolean {
  return INVALID_TAB_PATHS.some((p) => key === p || key.startsWith(`${p}/`))
}

const DASHBOARD_TAB: ErpTab = { key: DASHBOARD_PATH, title: '工作台', path: DASHBOARD_PATH }

const storageKey = (username: string) => `erp_tabs_${username}`

interface PersistedTabs {
  tabs: ErpTab[]
  activeKey: string
}

function loadPersisted(username: string): PersistedTabs | null {
  try {
    const raw = localStorage.getItem(storageKey(username))
    if (!raw) return null
    const data = JSON.parse(raw) as PersistedTabs
    if (!Array.isArray(data.tabs) || data.tabs.length === 0) return null
    // 剔除历史残留的无效页签（如 key="/" 及已删除路由的黑名单 path），并确保固定的工作台 Tab 永远存在且位于首位
    const tabs = data.tabs.filter(
      (t) => t.key !== DASHBOARD_PATH && !EXCLUDED_TAB_PATHS.includes(t.key) && !isInvalidTabPath(t.key),
    )
    tabs.unshift(DASHBOARD_TAB)
    const activeKey = tabs.some((t) => t.key === data.activeKey) ? data.activeKey : DASHBOARD_PATH
    return { tabs, activeKey }
  } catch {
    return null
  }
}

function persist(username: string, tabs: ErpTab[], activeKey: string) {
  try {
    localStorage.setItem(storageKey(username), JSON.stringify({ tabs, activeKey }))
  } catch {
    // localStorage 不可用时静默降级为内存态
  }
}

interface TabsState {
  username: string
  tabs: ErpTab[]
  activeKey: string
  /** 按用户名初始化（从 localStorage 恢复），用户名不变时为幂等操作 */
  init: (username: string) => void
  /** 打开（或激活）一个 Tab */
  openTab: (tab: ErpTab) => void
  /** 关闭 Tab，返回关闭后应激活的 key；工作台不可关闭 */
  closeTab: (key: string) => string
  /** 关闭除工作台外的所有 Tab，激活工作台 */
  closeAllTabs: () => void
  /** 关闭除工作台和指定 Tab（通常为当前激活页）外的所有 Tab */
  closeOtherTabs: (key: string) => void
  setActive: (key: string) => void
}

export const useTabsStore = create<TabsState>((set, get) => ({
  username: '',
  tabs: [DASHBOARD_TAB],
  activeKey: DASHBOARD_PATH,

  init: (username) => {
    if (get().username === username) return
    const restored = loadPersisted(username)
    set({
      username,
      tabs: restored?.tabs ?? [DASHBOARD_TAB],
      activeKey: restored?.activeKey ?? DASHBOARD_PATH,
    })
  },

  openTab: (tab) => {
    const { username, tabs } = get()
    const exists = tabs.some((t) => t.key === tab.key)
    const nextTabs = exists ? tabs : [...tabs, tab]
    persist(username, nextTabs, tab.key)
    set({ tabs: nextTabs, activeKey: tab.key })
  },

  closeTab: (key) => {
    if (key === DASHBOARD_PATH) return get().activeKey
    const { username, tabs, activeKey } = get()
    const index = tabs.findIndex((t) => t.key === key)
    if (index === -1) return activeKey
    const nextTabs = tabs.filter((t) => t.key !== key)
    let nextActive = activeKey
    if (activeKey === key) {
      // 关闭当前激活 Tab：优先右侧相邻，无右侧取左侧
      const neighbor = nextTabs[index] ?? nextTabs[index - 1]
      nextActive = neighbor ? neighbor.key : DASHBOARD_PATH
    }
    persist(username, nextTabs, nextActive)
    set({ tabs: nextTabs, activeKey: nextActive })
    return nextActive
  },

  closeAllTabs: () => {
    const { username } = get()
    const nextTabs = [DASHBOARD_TAB]
    persist(username, nextTabs, DASHBOARD_PATH)
    set({ tabs: nextTabs, activeKey: DASHBOARD_PATH })
  },

  closeOtherTabs: (key) => {
    const { username, tabs } = get()
    const keep = tabs.find((t) => t.key === key)
    const nextTabs = keep && key !== DASHBOARD_PATH ? [DASHBOARD_TAB, keep] : [DASHBOARD_TAB]
    persist(username, nextTabs, key)
    set({ tabs: nextTabs, activeKey: key })
  },

  setActive: (key) => {
    const { username, tabs } = get()
    persist(username, tabs, key)
    set({ activeKey: key })
  },
}))
